export interface AcquiredCamera {
  readonly video: HTMLVideoElement;
  /**
   * What the UA actually granted. The constraints below are `ideal` hints, so the negotiated stream
   * can differ from what was asked for — and since the detector is sized from it, that difference
   * is a real difference in per-frame cost rather than a cosmetic one.
   */
  settings(): MediaTrackSettings | null;
  /** Stops every track so the device's camera light goes off. */
  release(): void;
}

/**
 * Rear camera as a playing `<video>` element, on the constraints every canvas-rendered backend
 * needs (gyro and image tracking both self-render the feed as a background texture).
 *
 * Releases a partially-acquired camera on failure so the device light never sticks on — a stream
 * opened but never handed back before the throw is exactly the leak this guards against.
 */
export async function acquireRearCamera(): Promise<AcquiredCamera> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
    audio: false,
  });
  const release = (): void => {
    stream.getTracks().forEach((track) => track.stop());
  };
  // Read on demand rather than snapshotted: a track can be renegotiated mid-session, and a stale
  // copy would describe a stream that is no longer the one running.
  const settings = (): MediaTrackSettings | null => stream.getVideoTracks()[0]?.getSettings() ?? null;

  const video = document.createElement("video");
  try {
    video.playsInline = true;
    video.muted = true;
    video.autoplay = true;
    video.srcObject = stream;
    await video.play();
    await waitForDimensions(video);
    publishFrameSize(video);
    return { video, settings, release };
  } catch (error) {
    release();
    // Stopping the tracks halts decoding, but the element still holds the stream; detaching is
    // what lets a failed acquisition be collected instead of lingering on a retry.
    video.srcObject = null;
    throw error;
  }
}

/**
 * A raw `<video>`'s `width`/`height` are content *attributes*, distinct from its decoded
 * `videoWidth`/`videoHeight` — every consumer that draws this element as an image source by its
 * attributes (MindAR's input loader among them) reads a 0×0 region until these are published.
 * Left unset, every frame handed to a detector is blank while the camera feed still renders live
 * on screen — the tracker simply never locks, with nothing that looks like an error anywhere.
 *
 * Exported (not just used internally) so the benchmark harness's own camera acquisition
 * (`bench/camera.ts`, which needs its own stream-acquisition error messages and so cannot simply
 * delegate to `acquireRearCamera` wholesale) shares this exact fix rather than risking its own
 * copy silently drifting from it again.
 */
export function publishFrameSize(video: HTMLVideoElement): void {
  video.width = video.videoWidth;
  video.height = video.videoHeight;
}

/** A stream that opens but never decodes must not hold the camera open for the whole session. */
const DIMENSIONS_TIMEOUT_MS = 10_000;

/**
 * A resolved play() usually implies real dimensions, but not guaranteed on every UA; wait if not.
 *
 * Bounded, and terminal on media/track failure. A camera can be granted and still never produce a
 * decodable frame — another app holding the device, a track that ends immediately — and an
 * unbounded wait there keeps the stream alive while the caller sits on "Starting AR…" with nothing
 * to fall back from. Both dimensions are required: a nonzero width with a zero height still
 * divides by zero everywhere downstream.
 */
export function waitForDimensions(video: HTMLVideoElement, timeoutMs = DIMENSIONS_TIMEOUT_MS): Promise<void> {
  const ready = (): boolean => video.videoWidth > 0 && video.videoHeight > 0;
  if (ready()) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const track = (video.srcObject as MediaStream | null)?.getVideoTracks()[0];
    const settle = (finish: () => void): (() => void) => () => {
      clearTimeout(timer);
      video.removeEventListener("loadedmetadata", onProgress);
      video.removeEventListener("resize", onProgress);
      video.removeEventListener("error", onError);
      track?.removeEventListener("ended", onEnded);
      finish();
    };
    const onProgress = (): void => {
      if (ready()) settle(resolve)();
    };
    const onError = (): void => settle(() => reject(new Error("The camera stream failed before it produced a frame.")))();
    const onEnded = (): void => settle(() => reject(new Error("The camera track ended before it produced a frame.")))();
    const timer = setTimeout(
      () => settle(() => reject(new Error(`The camera did not produce a frame within ${timeoutMs}ms.`)))(),
      timeoutMs,
    );

    video.addEventListener("loadedmetadata", onProgress);
    video.addEventListener("resize", onProgress);
    video.addEventListener("error", onError);
    track?.addEventListener("ended", onEnded);
  });
}
